/**
 * Domain types for BabyOS Person Graph.
 * Mirror the Postgres schema exactly (snake_case in DB, camelCase in TS via mapping).
 * See supabase/migrations/*.sql for the source of truth.
 */

// ==================== ENUMS ====================

export type PersonType = 'adult' | 'child' | 'baby' | 'unborn'

export type IdentityStatus =
  | 'parent_controlled'
  | 'shared'
  | 'self_controlled'
  | 'archived'

export type WriterRole = 'user' | 'device'

export type EventSource = 'app' | 'watch' | 'siri' | 'import' | 'voice-confirm' | 'edge-function' | 'ingestion'

/**
 * Namespaced event types per pack (DEC-032).
 * Add a new type here BEFORE writing code that emits it.
 * Must match Postgres event_type strings.
 */
export type EventType =
  // Family lifecycle
  | 'family.created'
  | 'family.member.invited'
  | 'family.member.joined'
  | 'family.member.revoked'
  | 'family.handoff.created'
  // Person lifecycle
  | 'person.created'
  | 'person.updated'
  | 'person.archived'
  | 'person.identity.transitioned'
  // Early Care Pack
  | 'early_care.feeding.recorded'
  | 'early_care.feeding.corrected'
  | 'early_care.pump.recorded'
  | 'early_care.diaper.recorded'
  | 'early_care.diaper.corrected'
  | 'early_care.sleep.started'
  | 'early_care.sleep.ended'
  | 'early_care.medication.recorded'
  | 'early_care.medication.corrected'
  | 'early_care.bath.recorded'
  | 'early_care.temperature.recorded'
  | 'early_care.solid.recorded'
  // Health / Growth
  | 'growth.recorded'
  // Shopping
  | 'shopping.owned_item.acquired'
  | 'shopping.owned_item.status_changed'
  | 'shopping.purchase.recorded'
  | 'shopping.inventory.consumed'
  | 'shopping.inventory.replenished'
  // Needs
  | 'need.created'
  | 'need.resolved'
  | 'need.cancelled'
  // Cross-cutting
  | 'note.created'
  | 'milestone.recorded'

export type NeedCategory =
  | 'feeding'
  | 'clothing'
  | 'equipment'
  | 'document'
  | 'appointment'
  | 'education'
  | 'activity'
  | 'health'
  | 'financial'
  | 'logistics'
  | 'safety'
  | 'maintenance'
  | 'other'

export type NeedStatus = 'open' | 'in_progress' | 'resolved' | 'deferred' | 'cancelled'
export type NeedUrgency = 'low' | 'normal' | 'high' | 'critical'
export type NeedRecurrence = 'daily' | 'weekdays' | 'weekends' | 'weekly' | 'monthly'
export type NeedSourceType =
  | 'parent'
  | 'ai'
  | 'rule'
  | 'ingestion'
  | 'child_request'
  | 'organization'
  | 'system'
  | 'recall'

export type OwnedItemCategory =
  | 'clothing'
  | 'footwear'
  | 'sleep_gear'
  | 'feeding_gear'
  | 'safety_equipment'
  | 'transport'
  | 'furniture'
  | 'toy'
  | 'book'
  | 'electronic'
  | 'sports_equipment'
  | 'music_instrument'
  | 'school_supply'
  | 'document'
  | 'other'

export type OwnedItemStatus =
  | 'wish'
  | 'registered'
  | 'ordered'
  | 'in_use'
  | 'stored'
  | 'outgrown'
  | 'sold'
  | 'discarded'
  | 'recalled'
  | 'lost'

export type RoleInFamily =
  | 'owner'
  | 'parent'
  | 'caregiver'
  | 'temporary_caregiver'
  | 'viewer'

// ==================== JSONValue (typed payload tree) ====================

export type JSONValue =
  | null
  | boolean
  | number
  | string
  | JSONValue[]
  | { [key: string]: JSONValue }

// ==================== ENTITIES ====================

export interface User {
  id: string
  email: string
  display_name: string | null
  created_at: string
  updated_at: string
}

export interface Family {
  id: string
  display_name: string
  created_by: string
  active_packs: string[]
  created_at: string
  updated_at: string
}

export interface FamilyMember {
  id: string
  family_id: string
  user_id: string
  role: RoleInFamily
  status: 'active' | 'revoked' | 'expired'
  permissions: Record<string, unknown>
  expires_at: string | null
  invited_by: string | null
  joined_at: string
  revoked_at: string | null
}

export interface Person {
  id: string
  family_id: string
  display_name: string
  person_type: PersonType
  date_of_birth: string | null
  due_date: string | null
  sex: 'female' | 'male' | 'intersex' | 'unspecified' | null
  identity_status: IdentityStatus
  linked_user_id: string | null
  identity_transitioned_at: string | null
  metadata: Record<string, unknown>
  avatar_storage_ref: string | null
  cover_storage_ref: string | null
  created_at: string
  updated_at: string
}

/** Compute a person's age in months from date_of_birth. Null if not born. */
export function ageMonths(p: Person): number | null {
  if (!p.date_of_birth) return null
  const dob = new Date(p.date_of_birth)
  const now = new Date()
  const months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth())
  return months
}

export interface EventRow {
  id: string
  family_id: string
  person_id: string | null
  user_id: string
  device_id: string
  writer_role: WriterRole
  event_type: EventType
  payload: JSONValue
  payload_hash: string
  schema_version: number
  occurred_at: string
  recorded_at: string
  server_received_at: string | null
  server_sequence: number | null
  supersedes_event_id: string | null
  source: EventSource
}

export interface Need {
  id: string
  family_id: string
  person_id: string | null
  category: NeedCategory
  title: string
  description: string | null
  status: NeedStatus
  urgency: NeedUrgency
  source_type: NeedSourceType
  source_ref: string | null
  confidence: number
  due_at: string | null
  blocked_by_need_id: string | null
  resolved_at: string | null
  resolved_by_user_id: string | null
  resolution_note: string | null
  metadata: Record<string, unknown>
  recurrence: NeedRecurrence | null
  seeded_from: string | null
  created_at: string
  created_by_user_id: string | null
  updated_at: string
}

export interface OwnedItem {
  id: string
  family_id: string
  person_id: string | null
  product_catalog_id: string | null
  display_name: string
  category: OwnedItemCategory
  status: OwnedItemStatus
  size_label: string | null
  weight_limit_kg: number | null
  height_limit_cm: number | null
  purchase_id: string | null
  purchase_price: number | null
  purchased_at: string | null
  serial_number: string | null
  warranty_expires_at: string | null
  brand: string | null
  model: string | null
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface EarlyCareState {
  person_id: string
  family_id: string
  sleep_status: 'awake' | 'sleeping' | null
  sleep_started_at: string | null
  last_wake_at: string | null
  last_feed_at: string | null
  last_feed_type: string | null
  last_feed_volume_ml: number | null
  feed_count_24h: number
  feed_volume_24h_ml: number
  last_diaper_at: string | null
  last_diaper_type: string | null
  wet_count_24h: number
  dirty_count_24h: number
  last_medication_at: string | null
  last_medication_name: string | null
  last_event_sequence: number
  last_projected_at: string
}

export interface PersonState {
  person_id: string
  family_id: string
  life_stage: 'unborn' | 'newborn' | 'infant' | 'toddler' | 'preschool' | 'school_age' | 'preteen' | 'teen' | 'adult'
  age_days: number | null
  age_months: number | null
  active_packs: string[]
  open_needs_count: number
  unresolved_high_urgency_count: number
  last_event_sequence: number
  last_projected_at: string
}

// ==================== insert_event() RPC response ====================

export interface InsertEventAck {
  event_id: string
  server_sequence: number
  server_received_at: string
  accepted: boolean
  projection_version: number
  idempotent_hit: boolean
}

// ==================== check_recent_medication() RPC response ====================

export interface RecentMedicationCheck {
  recent_dose_count: number
  most_recent_at: string | null
  most_recent_by_user_id: string | null
  most_recent_amount: number | null
  most_recent_amount_unit: string | null
}
